import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { SceneLight, LightOrbit } from '@/worker/types';
import { computeOrbitPosition } from './SceneLighting';

function PointLightWireframe({
  light,
  opacity,
}: {
  light: import('@/worker/types').LightPoint;
  opacity: number;
}) {
  const lineSegments = useMemo(() => {
    const size = 2; // Crosshair size
    const p = new THREE.Vector3(
      light.position[0],
      light.position[1],
      light.position[2],
    );
    const points = [
      new THREE.Vector3(p.x - size, p.y, p.z),
      new THREE.Vector3(p.x + size, p.y, p.z),
      new THREE.Vector3(p.x, p.y - size, p.z),
      new THREE.Vector3(p.x, p.y + size, p.z),
      new THREE.Vector3(p.x, p.y, p.z - size),
      new THREE.Vector3(p.x, p.y, p.z + size),
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const color = new THREE.Color(
      light.color[0],
      light.color[1],
      light.color[2],
    );
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
    });
    return new THREE.LineSegments(geometry, material);
  }, [light, opacity]);

  return <primitive object={lineSegments} />;
}

function DirectionalLightWireframe({
  light,
  opacity,
}: {
  light: import('@/worker/types').LightDirectional;
  opacity: number;
}) {
  const lineSegments = useMemo(() => {
    const size = 5;
    const length = 15;
    const p = new THREE.Vector3(
      light.position[0],
      light.position[1],
      light.position[2],
    );

    // Default OpenSCAD light direction is to origin
    const target = new THREE.Vector3(0, 0, 0);
    const dir = new THREE.Vector3().subVectors(target, p).normalize();

    // Create local coordinate system
    const up = new THREE.Vector3(0, 1, 0);
    if (Math.abs(dir.y) > 0.99) up.set(1, 0, 0); // Handle looking straight up/down

    const right = new THREE.Vector3().crossVectors(up, dir).normalize();
    const actualUp = new THREE.Vector3().crossVectors(dir, right).normalize();

    // Square plane at the position
    const tr = new THREE.Vector3()
      .copy(p)
      .addScaledVector(right, size)
      .addScaledVector(actualUp, size);
    const tl = new THREE.Vector3()
      .copy(p)
      .addScaledVector(right, -size)
      .addScaledVector(actualUp, size);
    const br = new THREE.Vector3()
      .copy(p)
      .addScaledVector(right, size)
      .addScaledVector(actualUp, -size);
    const bl = new THREE.Vector3()
      .copy(p)
      .addScaledVector(right, -size)
      .addScaledVector(actualUp, -size);

    const points = [
      // Plane
      tl,
      tr,
      tr,
      br,
      br,
      bl,
      bl,
      tl,
      // Rays
      p,
      new THREE.Vector3().copy(p).addScaledVector(dir, length),
      tl,
      new THREE.Vector3().copy(tl).addScaledVector(dir, length),
      tr,
      new THREE.Vector3().copy(tr).addScaledVector(dir, length),
      bl,
      new THREE.Vector3().copy(bl).addScaledVector(dir, length),
      br,
      new THREE.Vector3().copy(br).addScaledVector(dir, length),
    ];

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const color = new THREE.Color(
      light.color[0],
      light.color[1],
      light.color[2],
    );
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
    });
    return new THREE.LineSegments(geometry, material);
  }, [light, opacity]);

  return <primitive object={lineSegments} />;
}

function SpotLightWireframe({
  light,
  opacity,
}: {
  light: import('@/worker/types').LightSpot;
  opacity: number;
}) {
  const lineSegments = useMemo(() => {
    const p = new THREE.Vector3(
      light.position[0],
      light.position[1],
      light.position[2],
    );

    // Calculate distance and radius
    const target = new THREE.Vector3(0, 0, 0);
    // Spotlights with distance=0 in three.js have infinite range, but we need to draw something
    const drawDist = light.distance > 0 ? Math.min(light.distance, 50) : 30;

    const dir = new THREE.Vector3().subVectors(target, p).normalize();
    const radius = Math.tan(light.angle) * drawDist;
    const centerAtDist = new THREE.Vector3()
      .copy(p)
      .addScaledVector(dir, drawDist);

    // Create local coordinate system
    const up = new THREE.Vector3(0, 1, 0);
    if (Math.abs(dir.y) > 0.99) up.set(1, 0, 0);

    const right = new THREE.Vector3().crossVectors(up, dir).normalize();
    const actualUp = new THREE.Vector3().crossVectors(dir, right).normalize();

    const points: THREE.Vector3[] = [];

    // Circle
    const segments = 16;
    const prev = new THREE.Vector3();
    for (let i = 0; i <= segments; i++) {
      const theta = (i / segments) * Math.PI * 2;
      const x = Math.cos(theta) * radius;
      const y = Math.sin(theta) * radius;

      const pt = new THREE.Vector3()
        .copy(centerAtDist)
        .addScaledVector(right, x)
        .addScaledVector(actualUp, y);

      if (i > 0) {
        points.push(prev.clone(), pt.clone());
      }
      prev.copy(pt);

      // Connect 4 points of the circle back to the light position
      if (i % 4 === 0) {
        points.push(p.clone(), pt.clone());
      }
    }

    // Central ray
    points.push(p.clone(), centerAtDist.clone());

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const color = new THREE.Color(
      light.color[0],
      light.color[1],
      light.color[2],
    );
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
    });
    return new THREE.LineSegments(geometry, material);
  }, [light, opacity]);

  return <primitive object={lineSegments} />;
}

// ---------------------------------------------------------------------------
// OrbitWireframe — draws the orbit ellipse path + animated position marker
// ---------------------------------------------------------------------------

const ORBIT_SEGMENTS = 64;

function OrbitWireframe({
  anchor,
  orbit,
  color,
  opacity,
  paused = false,
}: {
  anchor: [number, number, number];
  orbit: LightOrbit;
  color: THREE.Color;
  opacity: number;
  paused?: boolean;
}) {
  // Geometry for the full ellipse path (static shape, but orientation can
  // drift over time so we update it each frame when driftSpeed != 0)
  const ellipseRef = useRef<THREE.LineSegments>(null);
  // Geometry for the animated position marker (crosshair at current pos)
  const markerRef = useRef<THREE.LineSegments>(null);
  // Frozen elapsed time — captured on pause so the marker stays put
  const frozenTime = useRef<number | null>(null);

  const { invalidate } = useThree();

  // Build shared material
  const material = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthTest: false,
      }),
    [color, opacity],
  );

  // Build initial ellipse geometry (ORBIT_SEGMENTS line segments)
  const ellipseGeo = useMemo(() => {
    const pts: THREE.Vector3[] = [];
    const tmp = new THREE.Vector3();
    const prev = new THREE.Vector3();
    for (let i = 0; i <= ORBIT_SEGMENTS; i++) {
      const angleDeg = (i / ORBIT_SEGMENTS) * 360;
      computeOrbitPosition(anchor, orbit, angleDeg, 0, tmp);
      if (i > 0) {
        pts.push(prev.clone(), tmp.clone());
      }
      prev.copy(tmp);
    }
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, [anchor, orbit]);

  // Marker geometry: a small 3-axis crosshair (6 points = 3 line segments)
  const markerGeo = useMemo(() => {
    const s = Math.max(orbit.radius, orbit.radiusB > 0 ? orbit.radiusB : orbit.radius) * 0.06;
    const pts = [
      new THREE.Vector3(-s, 0, 0),
      new THREE.Vector3(s, 0, 0),
      new THREE.Vector3(0, -s, 0),
      new THREE.Vector3(0, s, 0),
      new THREE.Vector3(0, 0, -s),
      new THREE.Vector3(0, 0, s),
    ];
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, [orbit.radius, orbit.radiusB]);

  useFrame((state) => {
    if (paused) {
      if (frozenTime.current === null) {
        frozenTime.current = state.clock.getElapsedTime();
      }
      return;
    }
    frozenTime.current = null;

    const time = state.clock.getElapsedTime();
    const driftAngleDeg = time * orbit.driftSpeed;

    // Update ellipse path when orbit drifts
    if (orbit.driftSpeed !== 0 && ellipseRef.current) {
      const pts: THREE.Vector3[] = [];
      const tmp = new THREE.Vector3();
      const prev = new THREE.Vector3();
      for (let i = 0; i <= ORBIT_SEGMENTS; i++) {
        const angleDeg = (i / ORBIT_SEGMENTS) * 360;
        computeOrbitPosition(anchor, orbit, angleDeg, driftAngleDeg, tmp);
        if (i > 0) {
          pts.push(prev.clone(), tmp.clone());
        }
        prev.copy(tmp);
      }
      ellipseRef.current.geometry.setFromPoints(pts);
      ellipseRef.current.geometry.attributes.position.needsUpdate = true;
    }

    // Update marker position
    if (markerRef.current) {
      const angleDeg = time * orbit.speed * 360;
      const pos = new THREE.Vector3();
      computeOrbitPosition(anchor, orbit, angleDeg, driftAngleDeg, pos);
      markerRef.current.position.copy(pos);
    }

    invalidate();
  });

  return (
    <>
      <primitive object={new THREE.LineSegments(ellipseGeo, material)} ref={ellipseRef} />
      <primitive object={new THREE.LineSegments(markerGeo, material)} ref={markerRef} />
    </>
  );
}

export function LightWireframes({
  lights,
  wireframeOpacity,
  lightAnimationsPaused = false,
}: {
  lights: SceneLight[];
  wireframeOpacity: number;
  lightAnimationsPaused?: boolean;
}) {
  if (wireframeOpacity <= 0 || !lights || lights.length === 0) return null;

  return (
    <>
      {lights.map((light, index) => {
        const lightKey =
          'position' in light
            ? `${light.type}-${light.position.join(',')}`
            : `${light.type}-${index}`;

        const lightColor =
          'color' in light
            ? new THREE.Color(light.color[0], light.color[1], light.color[2])
            : new THREE.Color(1, 1, 1);

        // Orbit path visualization — rendered in addition to the static marker
        const orbitEl =
          'orbit' in light && light.orbit && 'position' in light ? (
            <OrbitWireframe
              key={`${lightKey}-orbit`}
              anchor={light.position}
              orbit={light.orbit}
              color={lightColor}
              opacity={wireframeOpacity}
              paused={lightAnimationsPaused}
            />
          ) : null;

        let lightEl: React.ReactNode = null;
        if (light.type === 'point') {
          lightEl = (
            <PointLightWireframe
              key={lightKey}
              light={light}
              opacity={wireframeOpacity}
            />
          );
        } else if (light.type === 'directional') {
          lightEl = (
            <DirectionalLightWireframe
              key={lightKey}
              light={light}
              opacity={wireframeOpacity}
            />
          );
        } else if (light.type === 'spot') {
          lightEl = (
            <SpotLightWireframe
              key={lightKey}
              light={light}
              opacity={wireframeOpacity}
            />
          );
        }

        if (!lightEl && !orbitEl) return null;
        return (
          <group key={lightKey}>
            {lightEl}
            {orbitEl}
          </group>
        );
      })}
    </>
  );
}
